<template>
    <div v-if="ready" class="GameView">
        <div class="game-view-container">
            <div class="top-part">
                <h5>Weekly quiz</h5>
                <h2>Waiting People to Join</h2>
            </div>
            <div class="players-ready-progress">
                <p> {{ players.length }}/{{ teamMembersList.length }} are Ready to Start</p>
                <div class="progress-bar-wrapper">
                    <div class="progress-bar" :style="{ width: percentageProgress }" />
                </div>
            </div>
            <button v-if="isOwner && !gameStarted && players.length" class="start-button" @click="startGame()">
                {{ game.name === 'game' ? 'Start Team Game' : 'Start Team Quiz' }}
            </button>
            <div v-else>
                <p v-if="teamGoal.shoutout" class="shoutout">{{ teamGoal.shoutout }}</p>
            </div>

            <div class="bottom-part">
                <img src="@/assets/images/mars.png" alt="mars" />
            </div>
        </div>
    </div>
</template>

<translations>
  playersJoined: '{count} Player Joined | {count} Players Joined'
  playersJoined_no: '{count} innlogget | {count} innlogget'
  playersCompleted: '{count} Player Completed | {count} Players Completed'
  playersCompleted_no: '{count} ferdig | {count} ferdige'

  startGame: 'Start'
  startGame_no: 'Start'
  endGame: 'Finish'
  endGame_no: 'Avslutt'
  revealResults: 'Results'
  revealResults_no: 'Resultater'
  joinGame: 'Join'
  joinGame_no: 'Bli med'
  waitingForGameToStart: 'Waiting for game to start ...'
  waitingForGameToStart_no: 'Venter på at spillet starter ...'
  waitingForGameToEnd: 'Waiting for game to end ...'
  waitingForGameToEnd_no: 'Venter på at alle er ferdige ...'
  waitingForResults: 'Waiting for results ...'
  waitingForResults_no: 'Venter på resultatet ...'
</translations>

<script>
import orderBy from 'lodash-es/orderBy';
import apolloClient from '@/apollo-client';
import gql from 'graphql-tag';

export default {
    props: {
        gameId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            adminMode: false,
            players: [],
            playersInLeaderboard: [],
            numberOfGameQuestions: 0,
            ready: false,
            teamMembersList: [],
            teamGoal: null
        };
    },
    computed: {
        enableStartGame() {
            return !this.game.running && !this.game.startedAt && !this.game.leaderboardReveal;
        },
        enableStopGame() {
            return this.game.running;
        },
        enableLeaderboard() {
            return !this.game.running && this.game.startedAt && !this.game.leaderboardReveal;
        },
        game() {
            return this.$store.state.moduleMemoransier.games.find(g => g.id === this.gameId);
        },
        me() {
            const userId = this.$store.state.moduleAuth.profile._id;
            return this.players.find(p => p.userId === userId);
        },
        progress() {
            return this.totalPlayerCount ? this.completedPlayerCount / this.totalPlayerCount : 0;
        },
        percentageProgress() {
            return Math.round((this.players.length / this.teamMembersList.length) * 100) + '%';
        },
        isOwner() {
            return this.game.owner === this.$store.state.moduleAuth.profile._id;
        },
        totalPlayerCount() {
            return this.players.length;
        },
        completedPlayerCount() {
            return this.players.filter(p => p.completed).length;
        },
        showResults() {
            return this.game && this.game.leaderboardReveal;
        },
        gameStarted() {
            return this.game.running;
        }
    },
    watch: {
        showResults(val) {
            this.refreshLeaderboard();
        }
    },
    async mounted() {
        await this.setTeamMembersListAndTeamGoals();

        await this.$store.dispatch('moduleMemoransier/refresh');
        const gameOptions = JSON.parse(this.game.options);

        this.numberOfGameQuestions = gameOptions.numberOfQuestions || 0;

        this.adminMode = this.me ? false : this.isOwner;

        this.joinGame();

        this.intervalId = setInterval(async () => {
            await this.refreshPlayers();
            await this.refreshGame();

            if (this.gameStarted && !this.isOwner) {
                this.game.name === 'game' ? this.goToGame() : this.goToQuiz();
            }
        }, 1000);

        this.ready = true;
    },

    beforeDestroy() {
        clearInterval(this.intervalId);
    },

    methods: {
        startGame() {
            if (this.enableStartGame) {
                this.$store.dispatch('moduleMemoransier/startGame', this.gameId);
            }

            this.goToPodiumView();
        },
        endGame() {
            if (this.enableStopGame) {
                this.$store.dispatch('moduleMemoransier/endGame', this.gameId);
                this.$store.dispatch('moduleMemoransier/revealLeaderboard', this.gameId);
            }

            this.exit();
        },
        async refreshGame() {
            await this.$store.dispatch('moduleMemoransier/refresh');
        },
        async refreshPlayers() {
            this.players = await this.$store.dispatch('moduleMemoransier/getGamePlayers', this.gameId);
        },

        joinGame() {
            if (!this.isOwner) {
                this.$store.dispatch('moduleMemoransier/joinGame', this.gameId);
            }
        },

        goToQuiz() {
            const options = JSON.parse(this.game.options);

            this.$router.push({
                name: 'MemoransierSessionQuizView',
                params: {
                    gameId: this.gameId,
                    courseId: options.courseId
                }
            });
        },

        goToGame() {
            this.$router.push({
                name: 'MemoransierSessionView',
                params: { gameId: this.gameId || this.$route.params.gameId }
            });
        },

        exit() {
            this.$router.push({
                name: 'DashboardHomeView'
            });
        },

        async setTeamMembersListAndTeamGoals() {
            const response = await apolloClient.query({
                fetchPolicy: 'no-cache',
                query: gql`
                    query {
                        teamLeaderboardFullsList {
                            avatar
                            braindates
                            displayName
                            teamId
                            teamName
                        }
                        teamGoalsList {
                            id
                            shoutout
                            start
                            stop
                            memoGameId
                            quizGameId
                            teamId
                            journeyId
                            createdBy
                        }
                    }
                `
            });

            this.teamMembersList = response.data.teamLeaderboardFullsList;
            this.teamGoal = response.data.teamGoalsList[0];
        },

        goToPodiumView() {
            this.$router.push({
                name: 'MemoransierPodiumView',
                props: {
                    gameId: this.gameId
                }
            });
        }
    }
};
</script>

<style scoped lang="scss">
.GameView {
    background: linear-gradient(180deg, #0a1335 0%, #1e1456 93.87%);
    display: flex;
    justify-content: center;
    .game-view-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        min-height: 100vh;
        justify-content: space-between;
        .top-part {
            margin-top: 180px;
            h5 {
                font-weight: normal;
                font-size: 14px;
                line-height: 22px;
                font-style: normal;
                text-align: center;
                color: #758ebf;
                margin-top: 0;
                margin-bottom: 20px;
            }

            h2 {
                font-style: normal;
                font-weight: 500;
                font-size: 22px;
                line-height: 26px;
                text-align: center;
                color: rgba(255, 255, 255, 0.8);
                margin-top: 0;
            }

            p {
                font-weight: normal;
                font-size: 14px;
                line-height: 22px;
                text-align: center;
                color: #758ebf;
            }
        }

        .players-ready-progress {
            font-weight: 500;
            font-size: 32px;
            line-height: 37px;
            color: #ffffff;
            .progress-bar-wrapper {
                background: #241a64;
                border-radius: 42px;
                .progress-bar {
                    height: 16px;
                    background: #40ace0;
                    border-radius: 42px;
                    transition: 0.5s;
                }
            }
        }

        .shoutout {
            font-weight: normal;
            font-size: 14px;
            line-height: 22px;
            color: #758ebf;
        }

        .start-button {
            width: 230px;
            height: 56px;
            background: #40ace0;
            border-radius: 5px;
            font-style: normal;
            font-weight: 500;
            font-size: 16px;
            line-height: 19px;
            border: none;
            color: #ffffff;
            cursor: pointer;
        }
    }
}
</style>
